import { useNavigate, useParams } from "@tanstack/react-router";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  FileCheck,
  HelpCircle,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import { useEffect } from "react";

import type { MediaItem } from "@/components/media-preview-modal/mediaPreviewModal.types";

import { Button } from "@/components/button";
import { MediaPreviewModalProvider } from "@/components/media-preview-modal/MediaPreviewModalProvider";
import { useMediaPreviewModal } from "@/components/media-preview-modal/useMediaPreviewModal";
import { FeaturePageLayout } from "@/features/shared/layout/FeaturePageLayout";
import { usePageTitle } from "@/hooks/usePageTitle";

import { ConfidenceCheckerBriefHistory } from "./components/ConfidenceCheckerBriefHistory";
import { useConfidenceCheckerSummaryQuery } from "./hooks/useConfidenceCheckerSummaryQuery";

export function ConfidenceCheckerSummaryPage() {
  return (
    <MediaPreviewModalProvider>
      <ConfidenceCheckerSummaryContent />
    </MediaPreviewModalProvider>
  );
}

function ConfidenceCheckerSummaryContent() {
  const params = useParams({ strict: false }) as {
    requestId?: number | string;
  };
  const requestId = Number(params.requestId);
  const navigate = useNavigate();
  const { openModal, setMediaItems } = useMediaPreviewModal();

  usePageTitle(`Confidence Checker Summary - #${requestId}`);

  const { data, error, isError, isLoading, refetch } =
    useConfidenceCheckerSummaryQuery(requestId);

  const briefAnalyses = data?.briefAnalyses ?? [];
  const currentBriefState = briefAnalyses.find(
    (analysis) => analysis.briefState != null,
  )?.briefState;
  const evaluations = data?.evaluations ?? [];

  const totalEvaluations = evaluations.length;
  const passedCount = evaluations.filter(
    (e) => e.evaluation?.displayStatus === "passed",
  ).length;
  const needsReviewCount = evaluations.filter(
    (e) => e.evaluation?.displayStatus === "needs_review",
  ).length;
  const overriddenCount = evaluations.filter(
    (e) => e.evaluation?.displayStatus === "overridden",
  ).length;

  useEffect(() => {
    const items: MediaItem[] = evaluations
      .filter((item) => Boolean(item.attachment.url))
      .map((item) => {
        const parts = item.attachment.name.split(".");
        const extension =
          parts.length > 1 ? (parts.at(-1)?.toLowerCase() ?? "") : "";

        return {
          category: item.attachment.category ?? undefined,
          extension,
          id: String(item.attachment.id),
          name: item.attachment.name,
          url: item.attachment.url ?? "",
        };
      });

    setMediaItems(items);
  }, [evaluations, setMediaItems]);

  if (isError) {
    return (
      <div className="p-xl">
        <FeaturePageLayout backButton title="Design Confidence Summary">
          <div className="mx-auto flex max-w-[600px] flex-col items-center justify-center gap-4 py-16 text-center">
            <div className="flex size-12 items-center justify-center rounded-full bg-bg-error-secondary text-text-error-primary">
              <AlertCircle size={24} />
            </div>
            <h2 className="text-xl font-bold text-text-primary">
              Failed to load summary
            </h2>
            <p className="max-w-[480px] text-sm text-text-tertiary">
              {(error as Error)?.message ||
                "An error occurred while fetching the confidence checker summary."}
            </p>
            <div className="flex gap-3 pt-2">
              <Button
                color="gray"
                onPress={() => navigate({ to: `/requests/${requestId}/edit` })}
                variant="secondary"
              >
                <ArrowLeft size={16} />
                Back to Edit
              </Button>
              <Button color="color" onPress={() => refetch()} variant="primary">
                <RefreshCw size={16} />
                Retry
              </Button>
            </div>
          </div>
        </FeaturePageLayout>
      </div>
    );
  }

  return (
    <div className="p-xl">
      <FeaturePageLayout
        backButton
        headerContent={
          <div className="flex w-full flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="rounded-md bg-bg-secondary px-2.5 py-1 text-xs font-semibold text-text-secondary border border-border-secondary">
                Request #{requestId}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <Button
                color="gray"
                onPress={() => refetch()}
                size="md"
                variant="secondary"
              >
                <RefreshCw size={15} />
                Refresh
              </Button>
              <Button
                color="color"
                onPress={() => navigate({ to: `/requests/${requestId}/edit` })}
                size="md"
                variant="secondary"
              >
                Go to Edit Request
              </Button>
            </div>
          </div>
        }
        isLoading={isLoading}
        title="Design Confidence Summary"
      >
        <div className="flex flex-col gap-6 p-6 lg:p-8 bg-bg-primary rounded-b-xl">
          {/* Summary Stat Highlights */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="flex items-center gap-3 rounded-xl border border-border-secondary bg-bg-primary p-4 shadow-sm">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-bg-brand-secondary text-text-brand-secondary">
                <Sparkles size={20} />
              </div>
              <div className="flex flex-col">
                <span className="text-xs font-medium text-text-tertiary">
                  Brief State
                </span>
                <span className="text-sm font-bold text-text-primary capitalize">
                  {currentBriefState ?? "Not analyzed"}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-3 rounded-xl border border-border-secondary bg-bg-primary p-4 shadow-sm">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-bg-secondary text-text-primary">
                <FileCheck size={20} />
              </div>
              <div className="flex flex-col">
                <span className="text-xs font-medium text-text-tertiary">
                  Total Evaluated
                </span>
                <span className="text-lg font-bold text-text-primary">
                  {totalEvaluations}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-3 rounded-xl border border-border-secondary bg-bg-primary p-4 shadow-sm">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-bg-success-secondary text-text-success-primary">
                <CheckCircle2 size={20} />
              </div>
              <div className="flex flex-col">
                <span className="text-xs font-medium text-text-tertiary">
                  Auto-checked / Passed
                </span>
                <span className="text-lg font-bold text-text-success-primary">
                  {passedCount}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-3 rounded-xl border border-border-secondary bg-bg-primary p-4 shadow-sm">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-bg-warning-secondary text-text-warning-primary">
                <HelpCircle size={20} />
              </div>
              <div className="flex flex-col">
                <span className="text-xs font-medium text-text-tertiary">
                  Needs Review / Override
                </span>
                <span className="text-lg font-bold text-text-warning-primary">
                  {needsReviewCount + overriddenCount}
                </span>
              </div>
            </div>
          </div>

          {/* Brief Analysis Section */}
          <ConfidenceCheckerBriefHistory
            briefAnalyses={briefAnalyses}
            evaluations={evaluations}
            onPreview={openModal}
          />
        </div>
      </FeaturePageLayout>
    </div>
  );
}
